import { RuleObject } from 'antd/es/form';
import Api from '../../services/Api';
import i18n from 'i18next';
import {
  DomainMode,
  shouldDeferValidationErrorWhileTyping,
  ValidationPhase,
  validateDomainOrUrl
} from '../domain-url/DomainUrlValidation';
import { translateDomainValidationErrorKey } from '../domain-url/translateDomainValidationErrorKey';

let debounceTimeout: NodeJS.Timeout | null = null;
const DEBOUNCE_DELAY = 500; // milliseconds

/**
 * Returns an Ant Design form validator rule to check if a website URL exists.
 * Uses debouncing to avoid excessive API calls.
 *
 * @param invalidFormatMessage Error message for invalid URL format.
 * @param notExistsMessage Error message if the website doesn't exist or is unreachable.
 * @param validationFailedMessage Generic error message for validation failure.
 * @returns Ant Design validator configuration object.
 */
export default function WebsiteValidator(
  invalidFormatMessage: string = 'Please enter a valid URL format (e.g., example.com).',
  notExistsMessage: string = i18n.t(
    'Website does not seem to exist or is unreachable.'
  ),
  validationFailedMessage: string = 'Failed to validate website.',
  options: {
    mode?: DomainMode;
    getValidationPhase?: () => ValidationPhase;
  } = {}
) {
  return ({}) => ({
    validator: (_: RuleObject, value: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (debounceTimeout) {
          clearTimeout(debounceTimeout);
        }

        if (!value) {
          // Don't validate empty values, let 'required' rule handle it
          return resolve();
        }

        debounceTimeout = setTimeout(async () => {
          try {
            const phase = options.getValidationPhase?.() || 'typing';
            const mode = options.mode || 'user_has_existing_website_domain';
            const localValidation = validateDomainOrUrl({
              value,
              mode,
              phase
            });

            if (!localValidation.isValid) {
              if (
                shouldDeferValidationErrorWhileTyping(
                  localValidation.errorKey,
                  phase
                )
              ) {
                resolve();
                return;
              }

              const localMessage = localValidation.errorKey
                ? translateDomainValidationErrorKey(
                    i18n.t.bind(i18n),
                    localValidation.errorKey
                  )
                : invalidFormatMessage;
              reject(new Error(localMessage));
              return;
            }

            if (!localValidation.isComplete) {
              resolve();
              return;
            }

            const result = await Api.validateWebsite(value); // Use original value for API
            if (result.data.valid) {
              resolve();
            } else {
              reject(new Error(notExistsMessage));
            }
          } catch (error: any) {
            // Handle URL format errors or API errors
            if (error instanceof TypeError) {
              const phase = options.getValidationPhase?.() || 'typing';
              if (phase === 'typing') {
                resolve();
                return;
              }
              reject(new Error(invalidFormatMessage));
            } else {
              reject(new Error(error.message || validationFailedMessage));
            }
          } finally {
            debounceTimeout = null; // Clear timeout reference after execution
          }
        }, DEBOUNCE_DELAY);
      });
    }
  });
}

// Keep the old export for compatibility, but mark as deprecated or remove if no longer needed elsewhere
/** @deprecated Use the default export WebsiteValidator function instead */
export const websiteValidator = WebsiteValidator()({
  getFieldValue: () => undefined
}); // Provide dummy getFieldValue
